import axios from 'axios';

const BASE_URL = '/account/domain';

/**
 * @typedef {import('axios').AxiosResponse} AxiosResponse
 */

/**
 * @typedef {{[key: string]: string}} DnsRecords
 */

/**
 * @typedef {object} Store
 * @property {string} storeId - The unique identifier for the store.
 * @property {string} email - The email associated with the store.
 * @property {boolean} emailVerification - Whether the email has been verified.
 * @property {boolean} isSupported - Whether domain authentication is supported for this email.
 */

/**
 * @typedef {object} DNSEntry
 * @property {boolean} isValid - Whether the DNS record is valid.
 * @property {string} type - The type of DNS record.
 * @property {string} host - The name associated with the DNS record.
 * @property {string} data - The data associated with the DNS record.
 */

/**
 * @typedef {object} DNSRecords
 * @property {DNSEntry} mailCName - The mail CNAME record.
 * @property {DNSEntry} dKim1 - The first DKIM record.
 * @property {DNSEntry} dKim2 - The second DKIM record.
 */

/**
 * @typedef {object} Domain
 * @property {string} [id] - The unique identifier for the domain record.
 * @property {string} name - The name associated with the domain record.
 * @property {string} domainVerificationStatus - The status of the domain record.
 * @property {DNSRecords} dnsRecords - The DNS records associated with the domain.
 * @property {boolean} isAuthenticated - Whether the domain has been authenticated.
 */

/**
 * @typedef {object} GetDomainsResponse
 * @property {Store[]} stores
 * @property {Domain[]} domains
 */

/**
 * @returns {Promise<AxiosResponse<GetDomainsResponse>>}
 */
export const getDomains = async () => {
  return axios.get(BASE_URL);
};

/**
 * @typedef {object} CreateDomainRequest
 * @property {string} email
 */

/**
 * @param {CreateDomainRequest} request
 * @returns {Promise<AxiosResponse<Domain>>}
 */
export const createDomain = async (request) => {
  return axios.post(BASE_URL, request);
};

/**
 * @typedef {object} ValidateDomainRequest
 * @property {string} email
 */

/**
 * @param {ValidateDomainRequest} request
 * @returns {Promise<AxiosResponse>}
 */
export const authenticateDomain = async (request) => {
  return axios.post(`${BASE_URL}/validate`, request);
};

/**
 * @typedef {object} UpdateDomainRequest
 * @property {string} id - The unique identifier for the domain record (GUID).
 * @property {string} subdomain - custom subdomain for the dns records
 * @property {string} selector - custom DKIM selector for the dns records
 */

/**
 * @param {UpdateDomainRequest} request
 * @returns {Promise<AxiosResponse<DNSRecords>>}
 */
export const updateDomain = async (request) => {
  return axios.put(`${BASE_URL}`, request);
};
